import { Button, Stack } from '@mui/material';
import { FieldArray, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import type { OrgFeatureModel } from 'generated/types';

import PatientPlanCard from './PatientPlanCard';
import { makeEmptyPlan } from './plan-helpers';
import type { PatientPlanValue } from './types';

const messages = defineMessages({
  addPlan: {
    id: 'patientPlans.addPlan',
    defaultMessage: 'Add plan',
  },
});

interface PatientPlanListProps {
  /** Field path of the plans array in the enclosing Formik form */
  name?: string;
  /** The plans as they currently stand, from the enclosing form's values */
  plans: PatientPlanValue[];
  /** Patient features a plan may include */
  eligibleFeatures: OrgFeatureModel[];
  disabled?: boolean;
  /** Seeds a plan added from "+ Add plan"; override to change the defaults */
  makePlan?: () => PatientPlanValue;
}

/**
 * Editable list of patient plans: add, remove, reorder, copy, expand. Writes
 * through `FieldArray`, so the consumer owns the form and its chrome.
 */
export default function PatientPlanList({
  name = 'plans',
  plans,
  eligibleFeatures,
  disabled,
  makePlan = makeEmptyPlan,
}: PatientPlanListProps) {
  const { formatMessage } = useIntl();
  const { setFieldValue } = useFormikContext();
  // Which cards are open. Keyed by local_id so it survives reordering and
  // removal; UI state, so it is deliberately not part of the form values.
  const [expandedPlans, setExpandedPlans] = React.useState<
    Record<string, boolean>
  >({});

  function toggleExpanded(localId: string) {
    setExpandedPlans((current) => ({
      ...current,
      [localId]: !current[localId],
    }));
  }

  return (
    <FieldArray name={name}>
      {({ move, push, remove }) => (
        <Stack gap={2}>
          {plans.map((plan, index) => {
            const namePrefix = `${name}[${index}]`;
            return (
              <PatientPlanCard
                disabled={disabled}
                eligibleFeatures={eligibleFeatures}
                expanded={Boolean(expandedPlans[plan.local_id])}
                index={index}
                key={plan.local_id}
                namePrefix={namePrefix}
                onCopyFrom={(source) =>
                  setFieldValue(`${namePrefix}.feature_ids`, [
                    ...source.feature_ids,
                  ])
                }
                onMoveDown={
                  index < plans.length - 1
                    ? () => move(index, index + 1)
                    : undefined
                }
                onMoveUp={index > 0 ? () => move(index, index - 1) : undefined}
                onRemove={() => remove(index)}
                onSetFeatureIds={(featureIds) =>
                  setFieldValue(`${namePrefix}.feature_ids`, featureIds)
                }
                onToggleExpanded={() => toggleExpanded(plan.local_id)}
                plan={plan}
                plans={plans}
              />
            );
          })}

          <Button
            data-testid="patient-plan-add"
            disabled={disabled}
            onClick={() => {
              const plan = makePlan();
              // A plan the admin just asked for opens ready to fill in.
              setExpandedPlans((current) => ({
                ...current,
                [plan.local_id]: true,
              }));
              push(plan);
            }}
            startIcon={<Icon name="plus" type="fas" />}
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              py: 1.5,
              width: '100%',
            }}
            type="button"
            variant="text"
          >
            {formatMessage(messages.addPlan)}
          </Button>
        </Stack>
      )}
    </FieldArray>
  );
}
